import { MOBILE_MEDIA } from '@/constants/breakpoints';

// Desplazamiento de la zona visible respecto al inicio de la página (visualViewport.offsetTop). En Chrome iOS
// la ventana se desplaza sola ~40 px (la página mide 643 y la zona visible 603) justo después de entrar a una
// pantalla, aunque el documento esté bloqueado: el encabezado queda cortado. Las pantallas bloqueadas
// (html.app-doc-locked, ver index.css) usan --app-vv-top para seguir a la zona visible en vez de quedarse atrás.
const applyVisualTop = () => {
  try {
    const top = window.visualViewport ? Math.max(0, Math.round(window.visualViewport.offsetTop)) : 0;
    document.documentElement.style.setProperty('--app-vv-top', `${top}px`);
  } catch (e) {
    // sin la variable el cuerpo queda en top: 0
  }
};

// Altura REAL de la zona visible (visualViewport.height), expuesta como la variable CSS --app-vh.
//
// Las unidades vh/dvh no sirven para ajustar una pantalla completa en el navegador de un iPhone (Safari y
// Chrome): no coinciden con la zona visible cuando las barras del navegador están desplegadas. La página
// queda más alta que la pantalla (medido en un iPhone 11: ~40 px de más), el documento se puede desplazar y el
// encabezado se oculta a medias mientras la pestaña del panel inferior queda debajo de la barra. La zona
// visible real sí coincide y se actualiza al cambiar las barras o el teclado (evento resize).
const apply = () => {
  try {
    // Solo en el layout mobile (ver constants/breakpoints.js). En desktop las variables valen lo mismo que antes
    // de existir (100vh, sin desplazamiento): ahí visualViewport cambia con el zoom del trackpad o la barra de
    // desplazamiento y no debe alterar la altura de las pantallas.
    if (!window.matchMedia(MOBILE_MEDIA).matches) {
      document.documentElement.style.setProperty('--app-vh', '100vh');
      document.documentElement.style.setProperty('--app-vv-top', '0px');
      return;
    }
    // Con el teclado abierto innerHeight no cambia pero la zona visible sí (visualViewport.height): la pantalla
    // debe ajustarse a esta última para que el contenido se desplace por dentro y los campos queden a la vista.
    const vv = window.visualViewport;
    const visible = vv ? Math.round(vv.height) : window.innerHeight;
    document.documentElement.style.setProperty('--app-vh', `${Math.min(visible, window.innerHeight) || window.innerHeight}px`);
    applyVisualTop();
  } catch (e) {
    // sin la variable los estilos usan su valor de respaldo (100vh)
  }
};

// Con el documento bloqueado el contenido se desplaza dentro de contenedores propios, y iOS no siempre desplaza
// ahí el campo enfocado cuando aparece el teclado: el campo queda debajo del espacio visible (medido en /shared:
// cuerpo de 323 px y campo en y=385). Se lleva el campo enfocado al centro del área visible.
let revealTimer = null;
const revealFocusedField = () => {
  clearTimeout(revealTimer);
  revealTimer = setTimeout(() => {
    const ae = document.activeElement;
    if (!ae || !/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) return;
    if (!document.documentElement.classList.contains('app-doc-locked')) return;
    const vv = window.visualViewport;
    const r = ae.getBoundingClientRect();
    const bottom = vv ? vv.height : window.innerHeight;
    if (r.top >= 0 && r.bottom <= bottom) return; // ya está a la vista
    try { ae.scrollIntoView({ block: 'center', inline: 'nearest' }); } catch (e) { /* navegador sin soporte */ }
  }, 150);
};

export const startAppViewportHeight = () => {
  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  if (window.visualViewport) window.visualViewport.addEventListener('resize', () => { apply(); revealFocusedField(); });
  if (window.visualViewport) window.visualViewport.addEventListener('scroll', applyVisualTop);
  window.addEventListener('scroll', applyVisualTop, { passive: true });
};
